import { ExternalV1GetTestNumbersResponse, ISearchRequest } from '@gr/shared/models';
import useAxios, { UseAxiosResult } from 'axios-hooks';

/**
 * Interface for the request parameters used to fetch test numbers
 * 
 * @property clientId - The client ID to filter by
 * @property clientName - The client name to filter by
 * @property number - The number to filter by
 * @property skip - The number of records to skip
 * @property take - The number of records to take
 */
export interface ITestNumberRequest {
  clientIds?: string;
  clientName?: string;
  number?: number;
  skip?: number;
  take?: number;
}

/**
 * Maps table/dropdown search options onto the query string that GET /v1/test-numbers expects.
 *
 * Exported so callers that re-issue the request themselves (e.g. the search callback in
 * TestNumbersDropdown) can build the same params instead of duplicating the mapping.
 * axios-hooks' refetch merges its override onto the original config, so a caller must
 * override `params` — overriding `data` would only attach an ignored body to the GET.
 *
 * @param tableOptions - Contains filter and pagination settings
 * @returns The query params for GET /v1/test-numbers
 */
export const mapTestNumberSearchParams = (tableOptions: ISearchRequest): ITestNumberRequest => {
  /**
   * Helper function to get the filter value for a given field name
   * @param fieldName - The name of the field to get the filter value for
   * @returns The filter value for the given field name, or undefined if the field is not found
   */
  const getFilterValue = (fieldName: string) =>
    tableOptions.filters.find(filter => filter.fieldName === fieldName)?.value;

  return {
    clientIds: getFilterValue('clientId')?.join(','),
    number: getFilterValue('number'),
    clientName: getFilterValue('clientName'),
    ...tableOptions.pagination
  };
};

/**
 * Custom React hook for fetching test numbers with filtering and pagination
 *
 * @param tableOptions - Contains filter and pagination settings from the data table
 * @returns UseAxiosResult - Returns the axios hook result containing data, loading state, and error state
 */
export default function useTestNumbers(
  tableOptions: ISearchRequest
): UseAxiosResult<ExternalV1GetTestNumbersResponse> {
  /**
   * Fetches the test numbers using the constructed search request
   * @returns The axios hook result containing data, loading state, and error state
   */
  return useAxios<ExternalV1GetTestNumbersResponse>({
    url: '/v1/test-numbers',
    method: 'GET',
    params: mapTestNumberSearchParams(tableOptions)
  });
};
